import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const CostBenefitAnalysis = () => {
  // State variables for Cost-Benefit Analysis calculation
  const [totalBenefits, setTotalBenefits] = useState(0); // Total benefits of the project
  const [totalCosts, setTotalCosts] = useState(0); // Total costs of the project
  const [netBenefit, setNetBenefit] = useState(0); // Net benefit (total benefits - total costs)

  // Event handlers to update state values
  const handleTotalBenefitsChange = (e) =>
    setTotalBenefits(parseFloat(e.target.value) || 0);
  const handleTotalCostsChange = (e) =>
    setTotalCosts(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate Net Benefit when any input changes
  useEffect(() => {
    // Calculate net benefit (total benefits - total costs)
    const calculatedNetBenefit = totalBenefits - totalCosts;
    setNetBenefit(calculatedNetBenefit);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [totalBenefits, totalCosts]);

  return (
    <Layout
      title="Cost-Benefit Analysis (CBA) Calculator"
      path="cost-benefit-analysis"
    >
      <section className="section-padding" id="cba-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Cost-Benefit Analysis Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Cost-Benefit Analysis (CBA) Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Total Benefits Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalBenefits" className="form-label">
                        Total Benefits
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Benefits:</strong> Enter the total value of all the benefits that are expected to be gained from the project or investment."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalBenefits"
                        placeholder="Enter total benefits"
                        value={totalBenefits || ""}
                        onChange={handleTotalBenefitsChange}
                      />
                    </div>

                    {/* Total Costs Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalCosts" className="form-label">
                        Total Costs
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Costs:</strong> Enter the total costs associated with the project or investment, including both fixed and variable costs."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalCosts"
                        placeholder="Enter total costs"
                        value={totalCosts || ""}
                        onChange={handleTotalCostsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Net Benefit:{" "}
                        <span className="fw-bold ms-3">
                          {netBenefit === 0
                            ? "0.00"
                            : netBenefit.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Cost-Benefit Analysis (CBA) Calculator
                </h3>

                {/* What is a Cost-Benefit Analysis (CBA) Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Cost-Benefit Analysis (CBA) Calculator?
                </h4>
                <p>
                  The **Cost-Benefit Analysis (CBA) Calculator** helps users
                  evaluate the feasibility of a project by comparing its total
                  benefits to its total costs. The calculator provides the **Net
                  Benefit** (or **Net Profit**) of a project, which helps in
                  understanding if the project is worthwhile from a financial
                  perspective.
                </p>

                {/* Why Use a Cost-Benefit Analysis (CBA) Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Cost-Benefit Analysis (CBA) Calculator?
                </h4>
                <p>
                  By using the CBA Calculator, decision-makers can determine if
                  the potential benefits of a project outweigh the costs. It
                  helps with making informed decisions about investments,
                  determining whether a project will be profitable, and
                  assessing the return on investment (ROI).
                </p>

                {/* Benefits of Using a Cost-Benefit Analysis (CBA) Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Cost-Benefit Analysis (CBA) Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps in making informed decisions on project investments:
                    </strong>{" "}
                    It enables users to assess whether a project is worth
                    pursuing based on its costs and benefits.
                  </li>
                  <li>
                    <strong>Provides insight into project ROI:</strong> The Net
                    Benefit helps to evaluate the overall return on investment.
                  </li>
                  <li>
                    <strong>
                      Useful for evaluating both tangible and intangible
                      benefits:
                    </strong>{" "}
                    CBA considers both measurable and less tangible outcomes,
                    such as community or environmental impacts.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Cost-Benefit Analysis (CBA) Calculator?
                </h4>
                <p>The Cost-Benefit Analysis Calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Project management teams:</strong> To assess the
                    feasibility of proposed projects and determine their
                    potential benefits.
                  </li>
                  <li>
                    <strong>Corporate planning departments:</strong> To make
                    informed decisions on investments and resource allocation.
                  </li>
                  <li>
                    <strong>Government agencies:</strong> To evaluate the costs
                    and benefits of public projects and initiatives.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  Why is Cost-Benefit Analysis Important for Projects?
                </h4>
                <p>
                  Cost-Benefit Analysis (CBA) is a critical tool for making
                  sound financial decisions. By analyzing the benefits and
                  costs, project leaders can ensure they are investing resources
                  wisely and focusing on projects that deliver the highest
                  value.
                </p>

                <h4 className="mt-4 mb-3">
                  How Do You Calculate the Net Benefit?
                </h4>
                <p>
                  The **Net Benefit** is calculated by subtracting the **Total
                  Costs** from the **Total Benefits** of a project. If the
                  result is positive, the project is considered to have a net
                  benefit, indicating that the benefits outweigh the costs.
                  Conversely, a negative result suggests the project might not
                  be viable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CostBenefitAnalysis;
